import PropTypes from 'prop-types';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/vi';
import 'dayjs/locale/en';

/**
 * Reusable Date Picker Component
 * Uses Material UI X DatePicker with custom styling
 * Displays formatted date based on culture with calendar icon
 *
 * @param {String} label - Date picker label
 * @param {String} value - Selected date value (YYYY-MM-DD format)
 * @param {Function} onChange - Change handler
 * @param {Boolean} required - Whether field is required
 * @param {Boolean} disabled - Whether date picker is disabled
 * @param {Boolean} fullWidth - Whether to take full width
 * @param {Object} sx - Additional Material UI sx styles
 * @param {Boolean} error - Whether field has an error
 * @param {String} helperText - Helper text below the input
 * @param {String} id - Element id
 * @param {String} name - Element name
 * @param {String} data-testid - Test attribute
 * @param {String} minDate - Minimum selectable date (YYYY-MM-DD format)
 * @param {String} maxDate - Maximum selectable date (YYYY-MM-DD format)
 */
function DatePicker({
  label,
  value,
  onChange,
  required = false,
  disabled = false,
  fullWidth = true,
  sx = {},
  error = false,
  helperText = '',
  id,
  name,
  'data-testid': dataTestId,
  minDate,
  maxDate,
  ...otherProps
}) {
  const { i18n } = useTranslation();

  // Set dayjs locale based on i18n language
  const locale = i18n.language === 'vi' ? 'vi' : 'en';

  // Convert value (YYYY-MM-DD string) to dayjs object
  const dayjsValue = value ? dayjs(value) : null;

  // Convert minDate and maxDate to dayjs objects
  // Default minDate to 2000-01-01 if not specified
  const dayjsMinDate = minDate ? dayjs(minDate) : dayjs('2000-01-01');
  // Default maxDate to today if not specified (to prevent future dates)
  const dayjsMaxDate = maxDate ? dayjs(maxDate) : dayjs();

  // Handle date change - convert dayjs object back to YYYY-MM-DD string
  const handleChange = (newValue) => {
    if (newValue && newValue.isValid()) {
      // Always format the date and pass it to validation
      // Let validation function handle the error messages
      const formattedDate = newValue.format('YYYY-MM-DD');
      // Create synthetic event to match existing onChange handlers
      const syntheticEvent = {
        target: {
          name: name,
          value: formattedDate,
        },
      };
      onChange(syntheticEvent);
    } else {
      // Invalid date format - pass empty string
      const syntheticEvent = {
        target: {
          name: name,
          value: '',
        },
      };
      onChange(syntheticEvent);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
      <MuiDatePicker
        label={label}
        value={dayjsValue}
        onChange={handleChange}
        disabled={disabled}
        minDate={dayjsMinDate}
        maxDate={dayjsMaxDate}
        slotProps={{
          textField: {
            required: required,
            fullWidth: fullWidth,
            error: error,
            helperText: helperText,
            variant: 'outlined',
            size: 'small',
            InputLabelProps: {
              shrink: true,
            },
            sx: {
              '& .MuiInputBase-root': {
                padding: '1px 2px',
                fontSize: '14px',
                lineHeight: '1.5',
              },
              '& .MuiInputBase-input': {
                padding: '7.5px 8px',
                fontSize: '14px',
                lineHeight: '1.5',
              },
              ...sx,
            },
            inputProps: {
              id: id,
              name: name,
              'data-testid': dataTestId,
            },
          },
          popper: {
            sx: {
              '& .MuiPaper-root': {
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              },
              '& .MuiPickersDay-root': {
                borderRadius: '8px',
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                },
                '&:hover': {
                  backgroundColor: 'primary.light',
                  color: '#fff',
                },
              },
              '& .MuiPickersCalendarHeader-root': {
                color: 'primary.main',
              },
              '& .MuiPickersCalendarHeader-switchViewButton': {
                color: 'primary.main',
              },
              '& .MuiPickersArrowSwitcher-button': {
                color: 'primary.main',
              },
              '& .MuiDayCalendar-weekDayLabel': {
                color: 'primary.main',
                fontWeight: 600,
              },
            },
          },
        }}
        {...otherProps}
      />
    </LocalizationProvider>
  );
}

DatePicker.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  sx: PropTypes.object,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  'data-testid': PropTypes.string,
  minDate: PropTypes.string,
  maxDate: PropTypes.string,
};

export default DatePicker;
